import type { GeometryElement } from 'jsxgraph';
import type { JsxgraphThemeService } from '../../jsxgraph-theme.service';
import type { ElementCreatorContext } from '../core/board.types';
import { parseThemeColor, resolveColor } from '../core/color.utils';
import { deepMerge } from '../core/deepMerge';
import { isElement } from '../core/element.utils';
import { bindLabel, createPointLabelContext, createPolygonLabelContext } from '../label/label.bind';
import { resolveLabelConfig } from '../label/label.model';
import { createPoint } from '../point/point.create';
import type { ElementTheme, LineStyle, PointStyle } from '../theme/theme.model';
import type { PolygonArgs, PolygonResult } from './polygon.model';
import { generateVertexNames } from './polygon.model';

/**
 * Resolve vertex style with proper propagation order:
 * 1. theme.point
 * 2. theme.polygon.vertex
 * 3. style.vertex
 * 4. style.vertexPerIndex[index]
 */
function resolveVertexStyle(theme: ElementTheme, styleVertex?: PointStyle, perIndex?: PointStyle): PointStyle {
  let result: PointStyle = { ...theme.point };
  result = deepMerge(result, theme.polygon.vertex);
  result = deepMerge(result, styleVertex);
  result = deepMerge(result, perIndex);
  return result;
}

/**
 * Resolve border style with proper propagation order:
 * 1. theme.line
 * 2. theme.polygon.border
 * 3. style.border
 * 4. style.borderPerEdge[index]
 */
function resolveBorderStyle(theme: ElementTheme, styleBorder?: LineStyle, perEdge?: LineStyle): LineStyle {
  let result: LineStyle = { ...theme.line };
  result = deepMerge(result, theme.polygon.border);
  result = deepMerge(result, styleBorder);
  result = deepMerge(result, perEdge);
  return result;
}

/**
 * Build JSXGraph polygon attributes from style configuration.
 */
function buildPolygonAttributes(
  themeService: JsxgraphThemeService,
  baseStyle: ElementTheme['polygon'],
  styleOverrides?: PolygonArgs['style'],
): Record<string, unknown> {
  const fillColor = resolveColor(themeService, styleOverrides?.fillColor ?? baseStyle.fillColor);
  const strokeColor = resolveColor(themeService, styleOverrides?.strokeColor ?? baseStyle.strokeColor);

  return {
    fillColor,
    fillOpacity: styleOverrides?.fillOpacity ?? baseStyle.fillOpacity,
    strokeColor,
    strokeWidth: styleOverrides?.strokeWidth ?? baseStyle.strokeWidth,
    visible: styleOverrides?.visible ?? true,
    withLabel: false,
    hasInnerPoints: false,
    // Vertex and border styles are handled through nested attributes
    vertices: { visible: false }, // We create vertices separately
    borders: { visible: false }, // We'll handle borders separately
  };
}

/**
 * Create a polygon element with vertices, borders, and optional labels.
 * Labels are now configured via the style property:
 * - style.label: Label for the polygon itself (centered)
 * - style.vertex.label: Default label for all vertices
 * - style.vertexPerIndex[i].label: Per-vertex label overrides
 *
 * Style propagation for vertices:
 * 1. theme.point (base point defaults)
 * 2. theme.polygon.vertex (polygon-specific vertex defaults)
 * 3. style.vertex (user-provided vertex defaults)
 * 4. style.vertexPerIndex[i] (per-vertex overrides)
 *
 * Style propagation for borders:
 * 1. theme.line (base line defaults)
 * 2. theme.polygon.border (polygon-specific border defaults)
 * 3. style.border (user-provided border defaults)
 * 4. style.borderPerEdge[i] (per-edge overrides)
 *
 * @param ctx - Element creator context (board, container)
 * @param themeService - Theme service for color resolution
 * @param theme - Element theme with defaults
 * @param args - Polygon creation arguments
 * @returns Polygon result with elements and optional labels
 */
export function createPolygon(
  ctx: ElementCreatorContext,
  themeService: JsxgraphThemeService,
  theme: ElementTheme,
  args: PolygonArgs,
): PolygonResult {
  const { board } = ctx;
  const { vertices: inputVertices, name = '', style, vertexNames } = args;

  const names = vertexNames ?? generateVertexNames(inputVertices.length);

  // Resolve or create vertices
  const vertices: GeometryElement[] = [];

  for (let i = 0; i < inputVertices.length; i++) {
    const input = inputVertices[i];

    if (isElement(input)) {
      vertices.push(input);
    } else if (typeof input === 'string') {
      const found = board.elementsByName[input];
      if (!found) {
        throw new Error(`Point "${input}" not found on board`);
      }
      vertices.push(found as GeometryElement);
    } else {
      // Create new point from coords
      const vertexStyle = resolveVertexStyle(theme, style?.vertex, style?.vertexPerIndex?.[i]);
      const result = createPoint(ctx, themeService, theme, {
        coords: input,
        name: names[i],
        style: vertexStyle,
      });
      vertices.push(result.point);
    }
  }

  // Build polygon attributes
  const polygonAttrs = buildPolygonAttributes(themeService, theme.polygon, style);

  // Create the polygon
  const polygon = board.create('polygon', vertices, polygonAttrs) as GeometryElement;

  // Get the borders created by the polygon
  const typedPolygon = polygon as unknown as { borders: GeometryElement[] };
  const borders = typedPolygon.borders ?? [];

  // Apply border styles
  for (let i = 0; i < borders.length; i++) {
    const borderStyle = resolveBorderStyle(theme, style?.border, style?.borderPerEdge?.[i]);
    const borderAttrs: Record<string, unknown> = {
      strokeColor: resolveColor(themeService, borderStyle.color),
      strokeWidth: borderStyle.strokeWidth,
      dash: borderStyle.dash,
      visible: borderStyle.visible ?? true,
    };

    const border = borders[i] as unknown as { setAttribute: (attrs: Record<string, unknown>) => void };
    border.setAttribute(borderAttrs);
  }

  const result: PolygonResult = { polygon, vertices, borders };

  // Determine color for labels (strip shade — labels always use shade 500)
  const labelColor = parseThemeColor(style?.fillColor ?? 'neutral').name;

  // Create vertex labels if configured via style hierarchy
  // Priority: style.vertexPerIndex[i].label > style.vertex.label
  const hasVertexLabels = style?.vertex?.label || Object.values(style?.vertexPerIndex ?? {}).some((v) => v?.label);
  if (hasVertexLabels) {
    result.vertexLabels = [];

    for (let i = 0; i < vertices.length; i++) {
      const vertexLabelConfig = style?.vertexPerIndex?.[i]?.label ?? style?.vertex?.label;

      const resolvedConfig = resolveLabelConfig(vertexLabelConfig, { color: labelColor });
      if (resolvedConfig) {
        const boundLabel = bindLabel({
          ctx,
          themeService,
          element: vertices[i],
          config: resolvedConfig,
          contextProvider: createPointLabelContext(names[i], vertices[i]),
        });
        result.vertexLabels.push(boundLabel);
      }
    }
  }

  // Create polygon label if configured - now from style.label
  const polygonLabelConfig = resolveLabelConfig(style?.label, { color: labelColor, position: 'center' });
  if (polygonLabelConfig && name) {
    // Create centroid point for label positioning
    const centroid = board.create('centroid', [polygon], { visible: false }) as GeometryElement;
    result.polygonLabel = bindLabel({
      ctx,
      themeService,
      element: centroid,
      config: polygonLabelConfig,
      contextProvider: createPolygonLabelContext(name, polygon),
    });
  }

  return result;
}
